import { useState } from "react";
import { Download, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAdminLanguage } from "@/i18n/AdminLanguage";

// Export selection is independent from every report/dashboard filter.
const words={
  es:{title:"Exportar a Excel",subtitle:"Pedidos, datos del cliente, QR, estado de ingreso, ventas y pagos de promotores.",
      label:"Datos para exportar",all:"Todas las fiestas",action:"Descargar Excel (.xlsx)",
      downloading:"Generando Excel…",note:"El archivo se genera con datos recientes de D1 en el momento de descargarlo. No se actualiza solo después de descargarlo.",
      privacy:"Contiene códigos QR y datos de clientes. Guárdalo de forma privada.",fail:"No se pudo descargar el Excel"},
  en:{title:"Export to Excel",subtitle:"Orders, customers, QR tickets, check-in status, promoter sales and payouts.",
      label:"Export scope",all:"All parties",action:"Download Excel (.xlsx)",downloading:"Preparing Excel…",
      note:"This workbook is generated from current D1 data each time you download it. An already downloaded file does not auto-update.",
      privacy:"Contains QR tokens and customer information. Keep this workbook private.",fail:"Could not download Excel"},
  ru:{title:"Экспорт в Excel",subtitle:"Заказы, клиенты, QR-билеты, статус входа, продажи и переводы промоутеров.",
      label:"Какие данные выгрузить",all:"Все вечеринки",action:"Скачать Excel (.xlsx)",downloading:"Создаём Excel…",
      note:"Файл формируется из актуальных данных D1 при каждом скачивании. Ранее скачанный файл не обновляется автоматически.",
      privacy:"Содержит QR-коды и данные клиентов. Храните файл конфиденциально.",fail:"Не удалось скачать Excel"}
};

export default function AdminKndlaExport({events=[]}){
  const {lang}=useAdminLanguage();
  const text=words[lang]||words.es;
  const [scope,setScope]=useState("all");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const download=async()=>{
    if(busy)return;
    setBusy(true);setError("");
    try{
      const response=await api.get("/kndla/export.xlsx",{params:{event_id:scope},responseType:"blob",timeout:60000});
      const contentType=String(response.headers?.["content-type"]||"");
      if(!contentType.includes("spreadsheetml"))throw new Error(text.fail);
      const blob=new Blob([response.data],{type:contentType});
      const url=window.URL.createObjectURL(blob);
      const link=document.createElement("a");
      link.href=url;
      link.download=`KNDLA_entradas_${scope==="all"?"todas":`fiesta_${scope}`}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(()=>window.URL.revokeObjectURL(url),1000);
    }catch(exc){
      let msg=fmtErr(exc,text.fail);
      if(exc?.response?.data instanceof Blob){
        try{const body=JSON.parse(await exc.response.data.text());msg=body.detail||body.error||msg;}
        catch(_ignored){}
      }
      setError(msg);
    }finally{setBusy(false);}
  };
  return <section className="rounded-2xl border border-orange-500/20 bg-ash/85 p-5" aria-label={text.title}>
    <div className="flex items-center gap-2"><FileSpreadsheet className="text-flame" size={20}/>
      <h3 className="font-syne text-lg font-bold text-white">{text.title}</h3></div>
    <p className="mt-2 text-xs leading-5 text-white/60">{text.subtitle}</p>
    <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-end">
      <label className="w-full flex-1 space-y-2"><span className="block text-[11px] font-bold uppercase tracking-wide text-white/50">{text.label}</span>
        <select value={scope} onChange={e=>setScope(e.target.value)} disabled={busy}
          className="w-full rounded-lg border border-white/15 bg-coal px-3 py-2.5 text-sm text-white outline-none focus:border-flame">
          <option value="all">{text.all}</option>
          {events.map(e=><option key={e.id} value={String(e.id)}>{e.name} · {e.event_date||''}</option>)}
        </select>
      </label>
      <button type="button" disabled={busy||!events.length} onClick={download}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-flame px-4 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">
        <Download size={16}/>{busy?text.downloading:text.action}
      </button>
    </div>
    <p className="mt-3 text-[11px] text-white/45">{text.note}</p>
    <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-300/85"><AlertTriangle size={13} className="mt-0.5 shrink-0"/>{text.privacy}</p>
    {error&&<p role="alert" className="mt-3 text-xs text-red-300">{error}</p>}
  </section>;
}
